/**
 * StatementDialog (#894) — the preview behind the "Statement" button on the
 * customer page and every Money → Balances row. Shows the lines the customer
 * will see (oldest first), the warnings that would get it rejected ("7 lines
 * have no work order number"), and three ways out: Download PDF, Download
 * Excel, Email — with a force-send confirm when a BLOCKING warning exists
 * (#1055; non-blocking ones are clean-up nudges and never hold the email).
 *
 * Lines the customer never sees — not due yet, or the job isn't finished
 * (#1224) — are listed under the table in their own muted panel. They're off
 * the PDF, the Excel and the email, but the office still sees the money is on
 * the books and why it's waiting.
 *
 * #1756 — a "Past due only / All open invoices" switch at the top picks what
 * the statement asks for. Past due only is the default and is what the
 * reminder cron sends; All open puts not-yet-due invoices on the preview, the
 * PDF, the Excel and the email too. Unfinished-job lines stay held either way.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { AlertTriangle, Download, Info, Loader2, Mail } from "lucide-react";
import { api, downloadAuthedFile } from "@/lib/api";
import { confirmDialog } from "@/components/ui/app-dialogs";
import { Dialog } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { SortHeaderButton } from "@/components/ui/sort-header-button";
import { formatDollars } from "@/lib/utils";
import { dateSortValue } from "@/lib/table-sort";
import { useTableControls } from "@/lib/use-table-controls";

type HoldReason = "not_due_yet" | "job_not_complete";

/** What the statement asks for (#1756). */
export type StatementScope = "past_due" | "all_open";

/** `?scope=all_open` on the preview/PDF/Excel; nothing for the default. */
export function statementScopeQuery(scope: StatementScope): string {
    return scope === "all_open" ? "?scope=all_open" : "";
}

const SCOPE_OPTIONS: { value: StatementScope; label: string; hint: string }[] = [
    { value: "past_due", label: "Past due only", hint: "What they owe today" },
    { value: "all_open", label: "All open invoices", hint: "Includes ones not due yet" },
];

/** The two-way switch at the top of the dialog — big, coloured, no reading needed. */
export function StatementScopeSwitch({
    value,
    onChange,
}: {
    value: StatementScope;
    onChange: (v: StatementScope) => void;
}) {
    return (
        <div
            role="radiogroup"
            aria-label="Which invoices go on the statement"
            className="inline-flex rounded-lg border bg-muted p-0.5"
            data-testid="statement-scope"
        >
            {SCOPE_OPTIONS.map((o) => {
                const on = value === o.value;
                return (
                    <button
                        key={o.value}
                        type="button"
                        role="radio"
                        aria-checked={on}
                        data-testid={`statement-scope-${o.value}`}
                        onClick={() => onChange(o.value)}
                        className={
                            on
                                ? "rounded-md bg-primary px-3 py-1.5 text-left text-primary-foreground shadow-sm"
                                : "rounded-md px-3 py-1.5 text-left text-foreground hover:bg-card"
                        }
                    >
                        <span className="block text-sm font-semibold leading-tight">{o.label}</span>
                        <span
                            className={on ? "block text-[11px] opacity-90" : "block text-[11px] text-muted-foreground"}
                        >
                            {o.hint}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}

interface StatementLine {
    invoice_id: string;
    invoice_number: string | null;
    job_display_number: string | null;
    wo_number: string | null;
    site_name: string | null;
    site_city: string | null;
    site_address: string | null;
    description: string;
    date: string;
    due_date?: string | null;
    days_late: number;
    past_due: boolean;
    balance: number;
    /** Set only on held-back lines — why the customer isn't seeing it. */
    hold_reason?: HoldReason | null;
}

/** Plain words for why a line is waiting. */
const HOLD_LABEL: Record<HoldReason, string> = {
    not_due_yet: "Not due yet",
    job_not_complete: "Job isn't finished",
};

interface StatementWarning {
    code: string;
    message: string;
    invoice_ids: string[];
    /** True = this stops the email until it's fixed or overridden. */
    blocks_send: boolean;
}

interface StatementPayload {
    statement: {
        customer: { id: string; company_name: string };
        as_of: string;
        scope?: StatementScope;
        lines: StatementLine[];
        totals: { total_due: number; past_due: number; invoice_count: number };
        warnings: StatementWarning[];
        held_back: {
            count: number;
            total: number;
            not_due_yet: number;
            job_not_complete: number;
            lines: StatementLine[];
        };
    };
}

interface ContactMethod {
    id: string;
    kind: string;
    value: string;
}
interface ContactWithMethods {
    id: string;
    firstName: string | null;
    lastName: string | null;
    methods: ContactMethod[];
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * What the customer is NOT being shown, and why (#1224). Deliberately muted
 * and below the table: it's a footnote for the office, not a problem to fix.
 * The money is still on the books — it just isn't a statement line yet.
 */
function HeldBackPanel({ held }: { held: NonNullable<StatementPayload["statement"]["held_back"]> }) {
    if (held.count === 0) return null;
    const why = [
        held.job_not_complete > 0 ? `${held.job_not_complete} on a job that isn't finished` : null,
        held.not_due_yet > 0 ? `${held.not_due_yet} not due yet` : null,
    ].filter(Boolean);
    return (
        <div className="rounded-md border bg-muted/40 px-3 py-2" data-testid="statement-held-back">
            <p className="flex items-start gap-2 text-xs text-muted-foreground">
                <Info className="mt-0.5 h-3.5 w-3.5 flex-none" />
                <span>
                    <span className="font-medium text-foreground">
                        {held.count} invoice{held.count === 1 ? "" : "s"} ({formatDollars(held.total)}) held back
                    </span>{" "}
                    — {why.join(", ")}. {held.count === 1 ? "It stays" : "They stay"} off the PDF, the Excel and the
                    email until the work is done and the money is due. Still on the books.
                </span>
            </p>
            <ul className="mt-1.5 space-y-0.5 pl-5">
                {held.lines.map((l) => (
                    <li key={l.invoice_id} className="flex flex-wrap gap-x-2 text-[11px] text-muted-foreground">
                        <Link to={`/invoices/${l.invoice_id}`} className="tabular-nums text-primary hover:underline">
                            {l.invoice_number ?? "—"}
                        </Link>
                        <span>·</span>
                        <span>{l.site_name || l.description}</span>
                        <span>·</span>
                        <span className="tabular-nums">{formatDollars(l.balance)}</span>
                        <span>·</span>
                        <span className="font-medium">{l.hold_reason ? HOLD_LABEL[l.hold_reason] : ""}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

export function StatementDialog({
    customerId,
    customerName,
    open,
    onClose,
}: {
    customerId: string;
    customerName: string;
    open: boolean;
    onClose: () => void;
}) {
    const qc = useQueryClient();
    const [recipients, setRecipients] = useState<string[]>([]);
    const [newEmail, setNewEmail] = useState("");
    const [emailOpen, setEmailOpen] = useState(false);
    const [scope, setScope] = useState<StatementScope>("past_due");

    const preview = useQuery({
        queryKey: ["customer-statement", customerId, scope],
        queryFn: () => api.get<StatementPayload>(`/api/customers/${customerId}/statement${statementScopeQuery(scope)}`),
        enabled: open,
    });

    const contacts = useQuery({
        queryKey: ["customer-contact-methods", customerId],
        queryFn: () => api.get<{ contacts: ContactWithMethods[] }>(`/api/customers/${customerId}/contact-methods`),
        enabled: open && emailOpen,
    });

    const statement = preview.data?.statement;
    const warnings = statement?.warnings ?? [];
    // Only the blocking ones ("they will reject these") stop the send and earn
    // the "send anyway?" confirm; the rest are clean-up nudges (#1055).
    const blockingWarnings = warnings.filter((w) => w.blocks_send);
    const hasLines = (statement?.lines.length ?? 0) > 0;
    const held = statement?.held_back;
    // No sort clicked = the statement's own oldest-first order.
    const {
        visible: lines,
        sortKey,
        sortDir,
        toggleSort,
    } = useTableControls(statement?.lines ?? [], {
        sortId: "customer.statement-lines",
        sortAccessors: {
            date: (l) => dateSortValue(l.date),
            invoice: (l) => l.invoice_number,
            wo: (l) => l.wo_number,
            site: (l) => l.site_name,
            address: (l) => l.site_address || l.site_city,
            days: (l) => (l.past_due ? l.days_late : null),
            due: (l) => l.balance,
        },
        descFirst: ["date", "days", "due"],
    });
    const sortHeader = (key: string, label: string) => (
        <SortHeaderButton
            label={label}
            active={sortKey === key}
            dir={sortDir}
            onClick={() => toggleSort(key)}
            className="uppercase tracking-wide"
        />
    );

    const send = useMutation({
        mutationFn: (force: boolean) =>
            api.post(`/api/customers/${customerId}/statement/send`, {
                ...(recipients.length > 0 ? { recipients } : {}),
                ...(force ? { force: true } : {}),
                scope,
            }),
        onSuccess: () => {
            toast.success(`Statement emailed to ${customerName}.`);
            qc.invalidateQueries({ queryKey: ["money-balances"] });
            qc.invalidateQueries({ queryKey: ["customer-statement", customerId] });
            setEmailOpen(false);
            onClose();
        },
        onError: (e: unknown) => {
            const err = e as { body?: { error?: string; message?: string } };
            if (err?.body?.error === "STATEMENT_WARNINGS") {
                toast.error(err.body.message ?? "This statement has warnings — review them, or send anyway.");
                return;
            }
            toast.error(err?.body?.message ?? "Couldn't email the statement.");
        },
    });

    const download = async (ext: "pdf" | "xlsx") => {
        try {
            await downloadAuthedFile(
                `/api/customers/${customerId}/statement.${ext}${statementScopeQuery(scope)}`,
                `Statement - ${customerName}${scope === "all_open" ? " (all open)" : ""}.${ext}`,
            );
        } catch {
            toast.error(`Couldn't download the ${ext === "pdf" ? "PDF" : "Excel file"}.`);
        }
    };

    const addRecipient = (value: string) => {
        const v = value.trim();
        if (!EMAIL_RE.test(v)) return;
        setRecipients((prev) => (prev.some((r) => r.toLowerCase() === v.toLowerCase()) ? prev : [...prev, v]));
        setNewEmail("");
    };

    return (
        <Dialog open={open} onClose={onClose} title={`Statement — ${customerName}`} className="max-w-4xl">
            <div className="mb-3">
                <StatementScopeSwitch value={scope} onChange={setScope} />
            </div>
            {preview.isLoading ? (
                <div className="flex justify-center py-16">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
            ) : !statement ? (
                <p className="py-10 text-center text-sm text-muted-foreground">
                    Couldn't load the statement. Close this and try again.
                </p>
            ) : !hasLines ? (
                // "Nothing to send" is not "nothing owed" — say which one it is
                // (#1224), or the office reads an open balance as a bug.
                held && held.count > 0 ? (
                    <div className="space-y-3 py-6">
                        <p className="text-center text-sm text-muted-foreground">Nothing to send {customerName} yet.</p>
                        <HeldBackPanel held={held} />
                    </div>
                ) : (
                    <p className="py-10 text-center text-sm text-muted-foreground">
                        {customerName} doesn't owe anything — there's no statement to send.
                    </p>
                )
            ) : (
                <div className="space-y-3">
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <div className="text-sm text-muted-foreground">As of {statement.as_of}</div>
                        <div className="text-right">
                            <div className="text-xl font-bold tabular-nums">
                                {formatDollars(statement.totals.total_due)}
                            </div>
                            <div className="text-xs text-muted-foreground">
                                {statement.totals.invoice_count} invoice
                                {statement.totals.invoice_count === 1 ? "" : "s"}
                                {statement.totals.past_due > 0 && (
                                    <span className="text-red-600 dark:text-red-400">
                                        {" "}
                                        · {formatDollars(statement.totals.past_due)} past due
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Two weights: a blocking warning stops the email until it's
                        fixed or overridden, a nudge is just worth tidying. */}
                    {warnings.map((w) => (
                        <div
                            key={w.code}
                            className={
                                w.blocks_send
                                    ? "flex items-start gap-2 rounded-md border border-amber-400/70 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200"
                                    : "flex items-start gap-2 rounded-md border px-3 py-2 text-xs text-muted-foreground"
                            }
                            data-testid={`statement-warning-${w.code.toLowerCase()}`}
                            data-blocks-send={w.blocks_send ? "true" : "false"}
                        >
                            {w.blocks_send ? (
                                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-none" />
                            ) : (
                                <Info className="mt-0.5 h-3.5 w-3.5 flex-none" />
                            )}
                            <span>{w.message}</span>
                        </div>
                    ))}

                    <div className="max-h-80 overflow-auto rounded-md border">
                        <table className="w-full text-xs">
                            <thead className="sticky top-0 bg-muted/80 backdrop-blur">
                                <tr className="text-left text-[10px] uppercase tracking-wide text-muted-foreground">
                                    <th className="px-2 py-1.5">{sortHeader("date", "Date")}</th>
                                    <th className="px-2 py-1.5">{sortHeader("invoice", "Invoice #")}</th>
                                    <th className="px-2 py-1.5">{sortHeader("wo", "WO / PO #")}</th>
                                    <th className="px-2 py-1.5">{sortHeader("site", "Site")}</th>
                                    <th className="px-2 py-1.5">{sortHeader("address", "Address")}</th>
                                    <th className="px-2 py-1.5">Work</th>
                                    <th className="px-2 py-1.5 text-right">{sortHeader("days", "Days")}</th>
                                    <th className="px-2 py-1.5 text-right">{sortHeader("due", "Due")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {lines.map((l) => (
                                    <tr key={l.invoice_id} className="border-t">
                                        <td className="px-2 py-1.5 whitespace-nowrap text-muted-foreground">
                                            {l.date}
                                        </td>
                                        <td className="px-2 py-1.5 tabular-nums">
                                            {/* Opens the invoice (faster paths 7b). */}
                                            <Link
                                                to={`/invoices/${l.invoice_id}`}
                                                className="text-primary hover:underline"
                                            >
                                                {l.invoice_number ?? "—"}
                                            </Link>
                                        </td>
                                        <td className="px-2 py-1.5 tabular-nums">
                                            {l.wo_number ?? (
                                                <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                                                    missing
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-2 py-1.5">{l.site_name || "—"}</td>
                                        {/* Same two cells the PDF prints, city as the address
                                            fallback. Date and Work stay on screen — the office
                                            uses them to spot lines to fix — but neither is on
                                            the customer's copy. */}
                                        <td className="px-2 py-1.5">{l.site_address || l.site_city || "—"}</td>
                                        <td className="px-2 py-1.5">{l.description}</td>
                                        <td className="px-2 py-1.5 text-right tabular-nums">
                                            {l.past_due ? (
                                                <span className="font-medium text-red-600 dark:text-red-400">
                                                    {l.days_late}
                                                </span>
                                            ) : l.days_late < 0 ? (
                                                // #1756 — on an "All open" statement.
                                                <span
                                                    className="whitespace-nowrap rounded-full bg-sky-100 px-1.5 py-0.5 text-[10px] font-medium text-sky-900 dark:bg-sky-900/40 dark:text-sky-100"
                                                    data-testid="statement-not-due"
                                                >
                                                    not due yet
                                                </span>
                                            ) : (
                                                "—"
                                            )}
                                        </td>
                                        <td className="px-2 py-1.5 text-right font-medium tabular-nums">
                                            {formatDollars(l.balance)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {held && <HeldBackPanel held={held} />}

                    {emailOpen && (
                        <div className="space-y-2 rounded-md border p-3">
                            <p className="text-xs font-medium">
                                Email to{" "}
                                <span className="text-muted-foreground">
                                    (leave empty to use the billing contact on file)
                                </span>
                            </p>
                            <div className="flex flex-wrap gap-1.5">
                                {recipients.map((r) => (
                                    <span
                                        key={r}
                                        className="inline-flex items-center gap-1 rounded-full border bg-muted px-2 py-0.5 text-xs"
                                    >
                                        {r}
                                        <button
                                            type="button"
                                            className="text-muted-foreground hover:text-foreground"
                                            onClick={() => setRecipients((prev) => prev.filter((x) => x !== r))}
                                        >
                                            ×
                                        </button>
                                    </span>
                                ))}
                            </div>
                            <div className="flex gap-2">
                                <input
                                    type="email"
                                    className="flex-1 rounded-md border bg-background px-2 py-1 text-xs"
                                    placeholder="Add an email address"
                                    value={newEmail}
                                    onChange={(e) => setNewEmail(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            addRecipient(newEmail);
                                        }
                                    }}
                                />
                                <Button size="sm" variant="outline" onClick={() => addRecipient(newEmail)}>
                                    Add
                                </Button>
                            </div>
                            {contacts.data && contacts.data.contacts.length > 0 && (
                                <div className="flex flex-wrap gap-1.5">
                                    {contacts.data.contacts.flatMap((c) =>
                                        c.methods
                                            .filter((m) => m.kind === "email")
                                            .map((m) => (
                                                <button
                                                    key={m.id}
                                                    type="button"
                                                    className="rounded-full border px-2 py-0.5 text-[11px] text-muted-foreground hover:bg-muted"
                                                    onClick={() => addRecipient(m.value)}
                                                >
                                                    {[c.firstName, c.lastName].filter(Boolean).join(" ") || "Contact"} ·{" "}
                                                    {m.value}
                                                </button>
                                            )),
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-3">
                        <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => download("pdf")}>
                                <Download className="mr-1 h-3.5 w-3.5" /> PDF
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => download("xlsx")}>
                                <Download className="mr-1 h-3.5 w-3.5" /> Excel
                            </Button>
                        </div>
                        {!emailOpen ? (
                            <Button size="sm" onClick={() => setEmailOpen(true)} data-testid="statement-email-open">
                                <Mail className="mr-1 h-3.5 w-3.5" /> Email statement
                            </Button>
                        ) : (
                            <Button
                                size="sm"
                                disabled={send.isPending}
                                data-testid="statement-email-send"
                                onClick={async () => {
                                    if (blockingWarnings.length > 0) {
                                        const ok = await confirmDialog({
                                            title: "Send with warnings?",
                                            message: `${blockingWarnings.map((w) => w.message).join("\n\n")}`,
                                            confirmLabel: "Send anyway",
                                        });
                                        if (!ok) return;
                                        send.mutate(true);
                                        return;
                                    }
                                    send.mutate(false);
                                }}
                            >
                                {send.isPending && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
                                Send it
                            </Button>
                        )}
                    </div>
                </div>
            )}
        </Dialog>
    );
}
